/**
 * AppHeader — global header per design README §3: product name top-left
 * (click = portfolio home = Escape Hatch), freshness + manual refresh for the
 * current data view, signed-in identity top-right with a sign-out action.
 *
 * ADR-003 D8: the signed-in slot also surfaces the auth kind. Customer users
 * (gateway /api/auth) see just their username; staff users (BFF /api/staff-auth)
 * see "Employee · Support" so they can confirm which surface they're on before
 * clicking sign out.
 *
 * Sign-out: a single click calls AuthStore.logout(), which dispatches to the
 * matching endpoint by authKind (employee → /api/staff-auth/logout;
 * customer → /api/auth/logout). Staff rejection keeps the identity and shows
 * an honest retryable alert. AuthGate shows login only after accepted logout
 * or a genuinely expired session (401).
 */

import { useState, type ReactNode } from 'react'
import type { PeriodSelection } from '@domain/types'
import { useAuthStore } from '@stores/authStore'
import { Link } from './Link'

export function AppHeader({ period, children }: { period: PeriodSelection; children?: ReactNode }) {
  const currentUser = useAuthStore((s) => s.currentUser)
  const authKind = useAuthStore((s) => s.authKind)
  const logout = useAuthStore((s) => s.logout)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [signOutFailed, setSignOutFailed] = useState(false)

  async function handleSignOut(): Promise<void> {
    setIsSigningOut(true)
    setSignOutFailed(false)
    try {
      await logout()
    } catch {
      // Never expose provider details or claim that the cookie was revoked.
      setSignOutFailed(true)
    } finally {
      setIsSigningOut(false)
    }
  }

  const isEmployee = authKind === 'employee'

  return (
    <header className="app-header" data-testid="app-header">
      <Link to="/" className="app-title" testid="app-title" period={period}>
        Smart Invoice Health
      </Link>
      <div className="app-header-controls">
        {children}
        {currentUser !== null ? (
          <div className="app-header-user" data-testid="app-header-user">
            <span
              className="app-header-user-name"
              data-testid="app-header-user-name"
              title={currentUser.displayName}
            >
              {currentUser.displayName}
            </span>
            <span
              className={
                isEmployee
                  ? 'app-header-user-kind app-header-user-kind-employee'
                  : 'app-header-user-kind'
              }
              data-testid="app-header-user-kind"
            >
              {isEmployee ? `Employee · ${currentUser.role}` : 'Customer'}
            </span>
            <button
              type="button"
              className="app-header-signout"
              onClick={() => { void handleSignOut() }}
              disabled={isSigningOut}
              data-testid="app-header-signout"
            >
              {isSigningOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        ) : null}
      </div>
      {signOutFailed && currentUser !== null ? (
        <div className="login-error app-header-error" role="alert" data-testid="app-header-signout-error">
          We could not sign you out. Your session may still be active. Try again.
        </div>
      ) : null}
    </header>
  )
}
